import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { MANUAL_FORM_SCHEMAS, REQUEST_TYPES } from "@/lib/crmConstants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RequestTypeBadge } from "@/components/Badges";

const teamAllowedTypes = (user) => {
  if (!user) return [];
  if (user.role === "admin") return Object.keys(REQUEST_TYPES);
  const map = {
    sales: ["contact_us_lead", "contact_us_general", "contact_us_feedback"],
    service: ["contact_us_complaint"],
    bulk_procurement: ["bulk_procurement"],
    dealership_bd: ["dealership"],
    csr: ["csr"],
    warranty: ["warranty"],
  };
  const s = new Set();
  (user.teams || []).forEach((t) => (map[t] || []).forEach((x) => s.add(x)));
  return Array.from(s);
};

export default function NewRecordPage() {
  const { user } = useAuth();
  const allowedTypes = teamAllowedTypes(user);
  const [type, setType] = useState(allowedTypes[0] || "");
  const [values, setValues] = useState({});

  // Reset values to empty strings for each field when type changes — keeps
  // form inputs (esp. shadcn Select) in controlled mode from mount.
  useEffect(() => {
    if (!type) { setValues({}); return; }
    const schema = MANUAL_FORM_SCHEMAS[type];
    const empty = {};
    (schema?.fields || []).forEach((f) => { empty[f.key] = ""; });
    setValues(empty);
  }, [type]);

  const [busy, setBusy] = useState(false);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modelsByCategory, setModelsByCategory] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/meta/states").then((r) => setStates(r.data));
    api.get("/meta/categories").then((r) => setCategories(r.data));
  }, []);

  const schema = type ? MANUAL_FORM_SCHEMAS[type] : null;

  const setField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const loadModels = async (category) => {
    if (!category || modelsByCategory[category]) return;
    const r = await api.get(`/meta/products?category=${encodeURIComponent(category)}`);
    setModelsByCategory((prev) => ({ ...prev, [category]: r.data }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!schema) return;
    setBusy(true);
    try {
      const payload = { ...(schema.hidden || {}), ...values };
      const { data } = await api.post("/records", { request_type: type, data: payload });
      toast.success(`Created ${data.ticket_id}`);
      nav(`/records/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create record");
    } finally {
      setBusy(false);
    }
  };

  const currentCategory = values.product_category || values.product_category_interest || "";
  const currentModels = modelsByCategory[currentCategory] || [];

  const renderField = (f) => {
    const val = values[f.key] || "";
    const commonProps = {
      "data-testid": `field-${f.key}`,
      value: val,
    };
    if (f.type === "textarea") {
      return (
        <Textarea
          {...commonProps}
          required={f.required}
          onChange={(e) => setField(f.key, e.target.value)}
          rows={3}
        />
      );
    }
    if (f.type === "select") {
      return (
        <Select value={val} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "state") {
      return (
        <Select value={val} onValueChange={(v) => setField(f.key, v)}>
          <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select state…" /></SelectTrigger>
          <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "category") {
      return (
        <Select
          value={val}
          onValueChange={(v) => { setField(f.key, v); loadModels(v); setField("product_model", ""); }}
        >
          <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Select category…" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    if (f.type === "model") {
      return (
        <Select
          value={val}
          onValueChange={(v) => setField(f.key, v)}
          disabled={!currentCategory}
        >
          <SelectTrigger data-testid={`field-${f.key}`}>
            <SelectValue placeholder={currentCategory ? "Select model…" : "Pick a category first"} />
          </SelectTrigger>
          <SelectContent>
            {currentModels.map((m) => <SelectItem key={m.sku} value={m.title}>{m.title}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        type={f.type}
        required={f.required}
        placeholder={f.placeholder}
        onChange={(e) => setField(f.key, e.target.value)}
        {...commonProps}
      />
    );
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <div className="data-label mb-1">Manual entry</div>
        <h1 className="font-heading font-black text-4xl tracking-tight">New record</h1>
        <p className="text-sm text-[#52525B] mt-2">
          Create a record on behalf of a customer. Tagged as <span className="font-mono">source=manual</span>.
          Sales / Service requests auto-assign by state.
        </p>
      </div>

      <div className="card-shell p-6 space-y-6">
        <div>
          <Label className="data-label mb-1.5 block">Request type</Label>
          <Select value={type} onValueChange={(v) => setType(v)}>
            <SelectTrigger data-testid="new-request-type-select">
              <SelectValue placeholder="Choose request type" />
            </SelectTrigger>
            <SelectContent>
              {allowedTypes.map((k) => (
                <SelectItem key={k} value={k}>{REQUEST_TYPES[k].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {type && (
            <div className="mt-2 flex items-center gap-2">
              <RequestTypeBadge requestType={type} />
              <span className="text-xs text-[#71717A]">→ routed to {REQUEST_TYPES[type].team.replace(/_/g, " ")}</span>
            </div>
          )}
        </div>

        {schema && (
          <form onSubmit={submit} className="space-y-4" data-testid="new-record-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {schema.fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label className="data-label mb-1.5 block">
                    {f.label} {f.required && <span className="text-[#DC2626]">*</span>}
                  </Label>
                  {renderField(f)}
                </div>
              ))}
            </div>
            <div className="pt-2 flex gap-2">
              <Button
                type="submit"
                disabled={busy}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="new-record-submit"
              >
                Create record
              </Button>
              <Button type="button" variant="outline" onClick={() => nav(-1)}>Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
